import { ladeGemischt } from '../persistence/pruefungsEinstellungen';
import { schreibeQrUeberschreibungen } from '../persistence/qrUeberschreibungen';
import type { Store } from '../persistence/port';
import { loadSettings, saveSetting } from '../persistence/settings';
import {
  KATALOG,
  LEER,
  ausRelay,
  type Gemischt,
  type Schluessel,
} from '../platform/einstellungen';
import type { ServerClient } from './client';
import { MESSAGE, TIME_ADD, mitteilungsText, zugabeSekunden } from './aufsicht';
import { INSTALL_DEMO, WIPE_DATA, installiereDemo, loescheDaten } from './befehle';
import { SCREENSHOT } from './bildschirmfoto';
import { UPLOAD_LOGS, type Ausgang } from './protokollArchiv';
import { holeNachrichten, type Nachricht } from './nachrichten';

/**
 * Geräteeinstellungen mit Relay abgleichen — melden, fernsetzen, quittieren.
 *
 * Relay-Spec `docs/superpowers/specs/2026-09-29-geraeteeinstellungen-design.md` (Relay-Repo),
 * Abschnitte 2, 5, 6 und 12. Nur im **Relay-Modus**; Classic kennt das nicht.
 *
 * **Melden** — `POST /rest/v1/client-setting/report` mit dem **Vollbild** aller Einstellungen
 * des Katalogs, als Zeichenketten. Gemeldet wird der **wirksame** Wert, also das Ergebnis der
 * Rangfolge (QR → MDM → iPad → App → Vorgabe), nicht das, was Relay zuletzt gesetzt hat: nur
 * so sieht die Verwaltung, ob ihr Wert greift. Relay hält das nur im Speicher; ein Delta ginge
 * nach dessen Neustart verloren.
 *
 * **Fernsetzen** — ein `SET_SETTINGS` aus `GET /rest/v1/message/pending` wirkt **wie ein
 * gescannter Einstellungscode** (Entscheidung des Nutzers, 2026-10-01): die Werte gehen in die
 * QR-Ablage und schlagen damit auch das MDM. `''` **leert** einen Text (`LEER`), `null` **setzt
 * zurück** — dann gelten wieder MDM, iPad-Seite und Vorgabe (Entscheidung des Relay-Nutzers,
 * 2026-10-01; s. `auswerten`). Ausgenommen ist `demo_install` (`NICHT_MELDEN`): `'true'` liest
 * die Beispielprüfungen ein wie `INSTALL_DEMO` (Entscheidung des Nutzers, 2026-10-08).
 *
 * **Quittieren** — erst nach dem Anwenden, danach sofort melden. Die neue Meldung **ist** die
 * Rückmeldung, dass der Befehl gewirkt hat.
 *
 * ⚠️ **Alles andere ist fernsetzbar, auch Passwörter und Serverzugang** (Entscheidung des Nutzers,
 * 2026-10-01). Ein falscher `server_url` sperrt das Gerät damit aus Relay aus, bis jemand vor
 * Ort die gescannten Werte verwirft (Einstellungsdialog) oder einen neuen Code scannt.
 */

export const MELDE_PFAD = '/rest/v1/client-setting/report';
export const SET_SETTINGS = 'SET_SETTINGS';

/** Die Befehlsarten, die Stage ausführt. Alles andere bleibt unquittiert liegen (`nachrichten.ts`). */
const BEKANNT = new Set([SET_SETTINGS, WIPE_DATA, INSTALL_DEMO, TIME_ADD, MESSAGE, UPLOAD_LOGS, SCREENSHOT]);

/* ------------------------------------------------------------------ Melden */

/**
 * Was Relay weder sieht noch setzt. `demo_install` ist ein Auslöser, keine Einstellung: er
 * liest die Beispielprüfungen ein und legt sich danach selbst zurück. Relay führt ihn als
 * Befehl `INSTALL_DEMO` (Entscheidung des Relay-Nutzers, 2026-10-01).
 */
const NICHT_MELDEN: ReadonlySet<Schluessel> = new Set<Schluessel>(['demo_install']);

/** Das Vollbild als Zeichenketten — `true`/`false`, Zahlen als Text, wie Relay sie führt. */
export function alsZeichenketten(g: Gemischt): Record<string, string> {
  const werte: Record<string, string> = {};
  for (const name of Object.keys(KATALOG) as Schluessel[]) {
    if (NICHT_MELDEN.has(name)) continue;
    werte[name] = String(g[name].wert);
  }
  return werte;
}

/** Dieselbe Mischung wie beim Start (`main.tsx`), samt den Stufen der ausgewählten Prüfung. */
export { ladeGemischt };

export async function meldeEinstellungen(client: ServerClient, g: Gemischt): Promise<number> {
  const antwort = await client.post<{ accepted?: number } | null>(MELDE_PFAD, { values: alsZeichenketten(g) });
  return antwort?.accepted ?? 0;
}

/* ------------------------------------------------------------------ Fällig */

/**
 * Ob eine Meldung aussteht. Zu Beginn **ja** — das ist die Meldung beim Start.
 *
 * ⚠️ **Modulweit, nicht im Takt.** Die Anlässe (Änderung, Resume) entstehen ausserhalb des
 * Takts, und ein neu gestarteter Takt soll sehen, was vorher liegen geblieben ist.
 */
let faellig = true;
let wecker: (() => void) | null = null;
let weckerFrist: ReturnType<typeof setTimeout> | null = null;

/** Eine Meldung ist fällig — beim nächsten Durchgang, und der kommt gleich. */
export function markiereMeldungFaellig(): void {
  faellig = true;
  // Gedämpft: ein Dialog, der fünf Werte nacheinander speichert, soll einen Durchgang wecken,
  // nicht fünf.
  if (weckerFrist) clearTimeout(weckerFrist);
  weckerFrist = setTimeout(() => {
    weckerFrist = null;
    wecker?.();
  }, 1000);
}

/** Der Takt meldet hier an, wie er sich wecken lässt; `null` meldet ab. */
export function setzeMeldeWecker(fn: (() => void) | null): void {
  wecker = fn;
}

/** Ob eine Meldung ansteht — dann wartet der Takt mit dem Abgleich nicht (`takt.ts`). */
export const meldungFaellig = (): boolean => faellig;

/** Nur für Tests. */
export function setzeFaellig(wert: boolean): void {
  faellig = wert;
}

/* ------------------------------------------------------------------ Fernsetzen */

export interface Auswertung {
  /** Was in die QR-Ablage geht: ein Wert, `LEER` (ausdrücklich leer) oder `null` (zurücksetzen). */
  setzen: Partial<Record<Schluessel, string | typeof LEER | null>>;
  /** Was übergangen wurde, in Worten fürs Dateilog. */
  verworfen: string[];
  /**
   * Nur die Namen, die Stage nicht kennt, sortiert — für die Warnung (Bitte des Nutzers,
   * 2026-10-08). Ohne Werte: bei einem unbekannten Namen weiss Stage nicht, ob er geheim ist.
   */
  unbekannt: string[];
  /** `demo_install: 'true'` war dabei — die Beispielprüfungen einlesen wie bei `INSTALL_DEMO`. */
  demo: boolean;
}

/**
 * Welcher Katalogschlüssel hinter einem Namen steht.
 *
 * ⚠️ **Nur Relays Namen** (Single Source of Truth, Entscheidung des Nutzers, 2026-10-08). Ein
 * alter Name (`brightness_max`) ist am Draht unbekannt und wird gemeldet. Alte Namen in den
 * Ablagen auf dem Gerät liest weiterhin `platform/alteNamen.ts` — die hat die App selbst
 * geschrieben, und Relay kann sie nicht sehen.
 */
function schluesselFuer(name: string): Schluessel | null {
  return Object.prototype.hasOwnProperty.call(KATALOG, name) ? (name as Schluessel) : null;
}

/**
 * Prüft die Werte eines `SET_SETTINGS`, ohne etwas zu schreiben.
 *
 * - **Ein Wert** wird mit derselben Regel geprüft wie MDM und QR (`pruefe`) — ein Wert, den die
 *   Rangfolge ohnehin verwürfe, gehört nicht in die Ablage, sonst läge dort eine Überschreibung,
 *   die nie greift und die niemand sieht.
 * - **`''` leert** (Entscheidung des Relay-Nutzers, 2026-10-01) — aber nur, wo leer etwas
 *   bedeutet, also bei Text. Bei Schalter, Auswahl und Zahl wird es übergangen.
 * - **`null` setzt zurück**: die Überschreibung fällt weg, es gelten wieder MDM, iPad-Seite
 *   und Vorgabe.
 */
export function auswerten(values: Record<string, unknown>): Auswertung {
  const setzen: Auswertung['setzen'] = {};
  const verworfen: string[] = [];
  const unbekannt: string[] = [];
  let demo = false;

  for (const [name, roh] of Object.entries(values)) {
    if (roh === undefined) continue;
    // Ein Auslöser, keine Einstellung: nichts in die Ablage, und `false`/`''`/`null` heisst
    // schlicht „nicht jetzt“.
    if (name === 'demo_install') {
      demo = roh === 'true' || roh === true;
      continue;
    }
    const schluessel = schluesselFuer(name);
    if (!schluessel || NICHT_MELDEN.has(schluessel)) {
      verworfen.push(name + ' (unbekannt)');
      unbekannt.push(name);
      continue;
    }
    if (roh === null) {
      setzen[schluessel] = null;
      continue;
    }
    if (typeof roh !== 'string') {
      verworfen.push(name + ' (kein Text)');
      continue;
    }
    const eintrag: { pruefe: (r: unknown) => unknown; erwartet: string; geheim: boolean; leerbar?: boolean } =
      KATALOG[schluessel];
    if (roh === '') {
      if (eintrag.leerbar) setzen[schluessel] = LEER;
      else verworfen.push(name + ' (leer, erwartet ' + eintrag.erwartet + ')');
      continue;
    }
    // Keine alten Werte am Draht (`METIS`, `dunkel`): Relay schickt nur noch seine.
    const umgewandelt = ausRelay({ [schluessel]: roh })[schluessel];
    const geprueft = eintrag.pruefe(umgewandelt);
    if (geprueft === null) {
      verworfen.push(name + ' (' + (eintrag.geheim ? '***' : roh) + ', erwartet ' + eintrag.erwartet + ')');
      continue;
    }
    setzen[schluessel] = String(geprueft);
  }
  return { setzen, verworfen, unbekannt: unbekannt.sort(), demo };
}


/** Die Nutzlast eines Befehls — Relay schickt sie als Objekt, ein Proxy mag sie als Text reichen. */
type Argument = {
  requestID?: string;
  values?: Record<string, unknown>;
  examID?: unknown;
  seconds?: unknown;
  text?: unknown;
  uploadID?: unknown;
  attachments?: unknown;
  screenshotID?: unknown;
};

function argumentVon(n: Nachricht): Argument | null {
  let arg = n.commandArgument;
  if (typeof arg === 'string') {
    try {
      arg = JSON.parse(arg);
    } catch {
      return null;
    }
  }
  return arg && typeof arg === 'object' ? (arg as Argument) : null;
}

/* ------------------------------------------------------------------ Schon erledigt */

/**
 * Die zuletzt angewendeten Nachrichten. Geht eine Quittung verloren, kommt derselbe Befehl
 * wieder — und dürfte dann eine Änderung überschreiben, die der Prüfer **danach** gemacht hat.
 *
 * Steht in der App-Ablage, weil er einen Neustart überleben muss; begrenzt, weil Relay einen
 * Befehl nach Ablauf seiner Frist ohnehin nicht mehr ausliefert.
 */
const ERLEDIGT = 'relay.erledigt';
const ERLEDIGT_MAX = 200;

async function leseErledigt(store: Store, clientId: string): Promise<string[]> {
  const roh = (await loadSettings(store, clientId))[ERLEDIGT];
  if (!roh) return [];
  try {
    const liste: unknown = JSON.parse(roh);
    return Array.isArray(liste) ? liste.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/* ------------------------------------------------------------------ Der Durchgang */

export interface AbgleichDeps {
  store: Store;
  clientId: string;
  client: ServerClient;
  log: (text: string) => Promise<void>;
  /** Bringt frisch gemischte Werte in die Oberfläche. Fehlt sie, wirken sie erst beim Neustart. */
  uebernehmen?: (g: Gemischt) => void;
  /**
   * Ist gerade eine Prüfung im Gang? Dann bleibt `WIPE_DATA` liegen, unquittiert, bis sie
   * vorbei ist (Entscheidung des Nutzers, 2026-10-01). Fehlt die Frage, gilt: nein.
   */
  bogenOffen?: () => boolean;
  /** Nach `WIPE_DATA`: welche Prüfungen weg sind — damit die Oberfläche sie vergisst. */
  geloescht?: (examIds: string[]) => void;
  /** Nach `INSTALL_DEMO`: wie viele Beispielprüfungen eingelesen wurden. */
  demoEingelesen?: (anzahl: number) => void;
  /**
   * `TIME_ADD`: gibt der laufenden Station Zeit. `'steht'`, wenn keine Station läuft — dann
   * wird nichts verlängert, aber quittiert (Entscheidung des Nutzers, 2026-10-07). Fehlt der
   * Rückruf, gilt `'steht'`.
   */
  zeitZugabe?: (sekunden: number) => 'laeuft' | 'wieder' | 'angehalten' | 'steht';
  /**
   * `MESSAGE`: hat der Prüfer sie gelesen und bestätigt? Dann wird jetzt quittiert. Sonst
   * zeigt `zeigeMitteilung` sie, und sie bleibt offen — Relay stellt sie im nächsten Abgleich
   * erneut zu. Fehlt eins von beiden, bleibt `MESSAGE` liegen wie eine unbekannte Art.
   */
  mitteilungBestaetigt?: (id: string) => boolean;
  /**
   * `SET_SETTINGS` trug Namen, die Stage nicht kennt — zum Zeigen und Protokollieren
   * (`platform/unbekannteEinstellungen.ts`). Ungültige Werte bekannter Namen gehören nicht dazu.
   */
  unbekannteEinstellungen?: (namen: string[]) => void;
  zeigeMitteilung?: (id: string, text: string) => void;
  /**
   * `UPLOAD_LOGS`: das Log-ZIP an Relay (`protokollArchiv.ts`). Quittiert wird bei `'fertig'`;
   * bei `'spaeter'` bleibt der Befehl liegen und kommt im nächsten Abgleich wieder. Fehlt der
   * Rückruf, bleibt er liegen wie eine unbekannte Art.
   */
  protokolleHochladen?: (uploadID: string, attachments: boolean) => Promise<Ausgang>;
  /**
   * `SCREENSHOT`: ein Bild der App an Relay (`bildschirmfoto.ts`). Ausgang wie bei
   * `protokolleHochladen`; fehlt der Rückruf, bleibt der Befehl liegen.
   */
  bildschirmfotoHochladen?: (screenshotID: string) => Promise<Ausgang>;
  /** Die Lebenszeichen-Parameter für `message/pending` (`nachrichten.ts`). */
  zustand?: () => Promise<Record<string, string | number | undefined>>;
}

/**
 * Ein Durchgang: Befehle holen (`SET_SETTINGS`, `WIPE_DATA`, `INSTALL_DEMO`, `TIME_ADD`, `MESSAGE`,
 * `UPLOAD_LOGS`, `SCREENSHOT`), ausführen,
 * quittieren, und melden, wenn etwas fällig ist.
 *
 * Wirft, wenn Relay nicht erreichbar ist — der Takt fängt das. Eine fällige Meldung bleibt dann
 * fällig.
 */
export async function gleicheEinstellungenAb(deps: AbgleichDeps): Promise<void> {
  let geaendert = false;

  await holeNachrichten({
    client: deps.client,
    log: deps.log,
    zustand: deps.zustand,
    uebergeben: async (nachrichten) => {
      const angenommen: string[] = [];
      const erledigt = await leseErledigt(deps.store, deps.clientId);

      for (const n of nachrichten) {
        if (!BEKANNT.has(n.commandType ?? '')) continue;
        // Schon ausgeführt, nur die Quittung ging verloren: noch einmal quittieren, nicht ausführen.
        if (erledigt.includes(n.id)) {
          angenommen.push(n.id);
          continue;
        }
        if (n.commandType === MESSAGE) {
          // Ohne Oberfläche (ein Test, ein künftiger Lauf ohne Bildschirm) bleibt sie liegen.
          if (!deps.zeigeMitteilung || !deps.mitteilungBestaetigt) continue;
          const text = mitteilungsText(argumentVon(n));
          if (text !== null && !deps.mitteilungBestaetigt(n.id)) {
            deps.zeigeMitteilung(n.id, text);
            continue;
          }
          // ⚠️ Den Text nicht ins Log: er kann Namen enthalten.
          await deps.log(
            'Relay: MESSAGE ' + n.id + (text === null ? ' ohne Text — übergangen.' : ' vom Prüfer bestätigt.'),
          );
          angenommen.push(n.id);
          erledigt.push(n.id);
          continue;
        }
        if (n.commandType === UPLOAD_LOGS) {
          if (!deps.protokolleHochladen) continue;
          const arg = argumentVon(n);
          const uploadID = arg?.uploadID;
          if (typeof uploadID !== 'string' || uploadID === '') {
            await deps.log('Relay: UPLOAD_LOGS ' + n.id + ' ohne uploadID — übergangen.');
          } else if (
            (await deps.protokolleHochladen(uploadID, arg?.attachments === true || arg?.attachments === 'true')) ===
            'spaeter'
          ) {
            continue;
          }
          angenommen.push(n.id);
          erledigt.push(n.id);
          continue;
        }
        if (n.commandType === SCREENSHOT) {
          if (!deps.bildschirmfotoHochladen) continue;
          const screenshotID = argumentVon(n)?.screenshotID;
          if (typeof screenshotID !== 'string' || screenshotID === '') {
            await deps.log('Relay: SCREENSHOT ' + n.id + ' ohne screenshotID — übergangen.');
          } else if ((await deps.bildschirmfotoHochladen(screenshotID)) === 'spaeter') {
            continue;
          }
          angenommen.push(n.id);
          erledigt.push(n.id);
          continue;
        }
        // Während einer Prüfung wird nichts gelöscht — liegen lassen, der nächste Takt fragt wieder.
        if (n.commandType === WIPE_DATA && deps.bogenOffen?.()) continue;
        angenommen.push(n.id);

        if (n.commandType === WIPE_DATA) {
          const examID = argumentVon(n)?.examID;
          const weg = await loescheDaten(deps.store, typeof examID === 'string' ? examID : undefined, deps.log);
          await deps.log('Relay: WIPE_DATA ' + n.id + ' — ' + String(weg.length) + ' Prüfung(en) gelöscht.');
          if (weg.length > 0) deps.geloescht?.(weg);
          erledigt.push(n.id);
          continue;
        }
        if (n.commandType === INSTALL_DEMO) {
          const anzahl = await installiereDemo(deps.store);
          await deps.log('Relay: INSTALL_DEMO ' + n.id + ' — ' + String(anzahl) + ' Beispielprüfungen eingelesen.');
          deps.demoEingelesen?.(anzahl);
          erledigt.push(n.id);
          continue;
        }
        if (n.commandType === TIME_ADD) {
          const sekunden = zugabeSekunden(argumentVon(n));
          if (sekunden === null) {
            await deps.log('Relay: TIME_ADD ' + n.id + ' ohne gültige Sekunden — übergangen.');
          } else {
            const wie = deps.zeitZugabe?.(sekunden) ?? 'steht';
            await deps.log(
              'Relay: TIME_ADD ' + n.id + ' — ' +
                (wie === 'steht'
                  ? 'keine laufende Station, nichts verlängert.'
                  : '+' + String(sekunden) + ' s (' + wie + ').'),
            );
          }
          erledigt.push(n.id);
          continue;
        }

        const values = argumentVon(n)?.values;
        if (!values || typeof values !== 'object') {
          await deps.log('Relay: SET_SETTINGS ' + n.id + ' ohne lesbare Werte — übergangen.');
        } else {
          const { setzen, verworfen, unbekannt, demo } = auswerten(values);
          if (unbekannt.length > 0) deps.unbekannteEinstellungen?.(unbekannt);
          await schreibeQrUeberschreibungen(deps.store, deps.clientId, setzen);
          if (Object.keys(setzen).length > 0) geaendert = true;
          await deps.log(
            'Relay: SET_SETTINGS ' + n.id + ' — ' + String(Object.keys(setzen).length) + ' gesetzt' +
              (verworfen.length > 0 ? ', übergangen: ' + verworfen.join(', ') : '') + '.',
          );
          if (demo) {
            const anzahl = await installiereDemo(deps.store);
            await deps.log('Relay: SET_SETTINGS ' + n.id + ' — demo_install, ' + String(anzahl) + ' Beispielprüfungen eingelesen.');
            deps.demoEingelesen?.(anzahl);
          }
        }
        erledigt.push(n.id);
      }

      if (angenommen.length > 0) {
        await saveSetting(deps.store, deps.clientId, ERLEDIGT, JSON.stringify(erledigt.slice(-ERLEDIGT_MAX)));
      }
      return angenommen;
    },
  });

  if (geaendert) {
    deps.uebernehmen?.(await ladeGemischt(deps.store, deps.clientId));
    faellig = true;
  }

  if (!faellig) return;
  // Vor dem Aufruf zurücksetzen: eine Änderung, die **während** der Meldung geschieht, setzt
  // das Merkzeichen neu und wird beim nächsten Durchgang gemeldet, statt verschluckt.
  faellig = false;
  try {
    const anzahl = await meldeEinstellungen(deps.client, await ladeGemischt(deps.store, deps.clientId));
    await deps.log('Relay: Einstellungen gemeldet (' + String(anzahl) + ' angenommen).');
  } catch (fehler) {
    faellig = true;
    throw fehler;
  }
}
